import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IUpdateOutletRequest {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
}

interface IUpdateOutletResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    accountType?: string;
  };

  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateOutletResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateOutletAsync = createAsyncThunk(
  'outlet/update',
  async (
    { id, name, address, state, city }: IUpdateOutletRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const appState = getState() as RootState;

      const accessToken = appState.auth.value?.data?.token?.accessToken;
      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IUpdateOutletResponse>(
        `/outlets/${id}/update`,
        { name, address, state, city },
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateOutletResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
          type: 'drawer',
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const outletUpdateSlice = createSlice({
  name: 'outlet/update',
  initialState,
  reducers: {
    resetOutletUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateOutletAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateOutletAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateOutletResponse;
      })
      .addCase(updateOutletAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateOutletResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetOutletUpdate } = outletUpdateSlice.actions;

export default outletUpdateSlice.reducer;
